import customPageConfigs from './custom_page_config.json';

export const ActivityTrackerManager = {
  globalEnabled: true,

  defaultConfig: {
    heartbeatInterval: 1000 * 60 * 5,
    warningBuffer: 60,
  },

  sessionTracker: null,
  currentConfig: null,

  getAdaptiveConfig: function() {
    const sessionTimeout = window.tokenExpiresIn || 1200;
    if (sessionTimeout < 300) {
      return {
        heartbeatInterval: Math.max(10000, (sessionTimeout * 1000) / 4),
        warningBuffer: Math.min(30, sessionTimeout / 2)
      };
    }

    return {...this.defaultConfig};
  },

  resolveTrackerConfig: function(config) {
    const adaptiveConfig = this.getAdaptiveConfig();
    return {
      ...this.defaultConfig,
      ...adaptiveConfig,
      ...(config.config || {}),
      pageContext: config.pageContext
    };
  },

  autoDetectionRules: [
    {
      pattern: '/admin/',
      pageContext: 'admin_general',
      enabled: false
    },
    {
      pattern: '/app/',
      pageContext: 'app_general',
      enabled: false
    },
    {
      pattern: '/reports/',
      pageContext: 'reports_general',
      enabled: false
    }
  ],

  customPageConfigs: customPageConfigs,

  initialize: function() {
    if (!this.globalEnabled) {
      return;
    }

    if (typeof ActivityTracker === 'undefined') {
      return;
    }

    const matchingConfig = this.findBestMatchingConfig();

    if (matchingConfig) {
      this.ensureSessionTracker(matchingConfig);
    }
  },


  findBestMatchingConfig: function() {
    for (const configKey of Object.keys(this.customPageConfigs)) {
      const config = this.customPageConfigs[configKey];
      if (this.shouldInitializeTracker(config)) {
        return { key: configKey, config: config, source: 'custom' };
      }
    }

    const currentPath = window.location.pathname;
    for (const rule of this.autoDetectionRules) {
      if (rule.enabled && currentPath.includes(rule.pattern)) {
        const autoConfig = {
          pageContext: rule.pageContext,
          instanceName: `${rule.pageContext.replace(/_/g, '')}ActivityTracker`
        };
        return { key: 'auto_detected', config: autoConfig, source: 'auto' };
      }
    }

    if (this.globalEnabled && typeof window.tokenExpiresIn !== 'undefined') {
      return {
        key: 'global_default',
        config: { pageContext: 'general', instanceName: 'globalActivityTracker' },
        source: 'global'
      };
    }

    return null;
  },

  ensureSessionTracker: function(matchingConfig) {
    const { key, config, source } = matchingConfig;

    const nextResolvedConfig = this.resolveTrackerConfig(config);
    if (this.sessionTracker && this.currentConfig && this.configsAreEquivalent(this.currentConfig.config, nextResolvedConfig)) {
      return;
    }

    if (this.sessionTracker) {
      this.stopSessionTracker();
    }

    this.initializeSessionTracker(key, config, source);
  },

  configsAreEquivalent: function(config1, config2) {
    return config1.pageContext === config2.pageContext &&
           config1.heartbeatInterval === config2.heartbeatInterval &&
           config1.warningBuffer === config2.warningBuffer;
  },

  initializeSessionTracker: function(configKey, config, source) {
    const instanceName = config.instanceName || `${configKey}ActivityTracker`;

    const trackerConfig = this.resolveTrackerConfig(config);

    try {
      this.sessionTracker = new ActivityTracker(trackerConfig);
      this.currentConfig = { key: configKey, config: trackerConfig, source: source, instanceName: instanceName };

      window.sessionActivityTracker = this.sessionTracker;
      window[instanceName] = this.sessionTracker;
    } catch (error) {
      console.error('[ActivityTrackerManager] init failed:', configKey, error);
    }
  },

  stopSessionTracker: function() {
    if (this.sessionTracker) {
      try {
        this.sessionTracker.stop();
      } catch (error) {
        console.error('[ActivityTrackerManager] stop failed:', error);
      }

      this.sessionTracker = null;

      if (this.currentConfig) {
        const instanceName = this.currentConfig.instanceName || `${this.currentConfig.key}ActivityTracker`;
        delete window[instanceName];
        this.currentConfig = null;
      }

      delete window.sessionActivityTracker;
    }
  },

  shouldInitializeTracker: function(config) {
    if (!config.conditions || config.conditions.length === 0) {
      return false;
    }

    const pathConditions = config.conditions.filter(c => c.type === 'pathname');
    if (pathConditions.length === 0) return false;
    return pathConditions.some(c => window.location.pathname.includes(c.pattern));
  },  
};

document.addEventListener('DOMContentLoaded', () => {
  ActivityTrackerManager.initialize();
});

window.addEventListener('beforeunload', () => {
  ActivityTrackerManager.stopSessionTracker();
});

window.ActivityTrackerManager = ActivityTrackerManager;
